import React, { PropsWithChildren, ReactNode, useId, useState } from 'react';
import Alert from '@material-ui/lab/Alert';
import Box from '@material-ui/core/Box';
import Collapse from '@material-ui/core/Collapse';
import Link from '@material-ui/core/Link';

/**
 * Props for {@link PageNotice}.
 *
 * @public
 */
export type PageNoticeProps = PropsWithChildren<{
  /**
   * More on the note - a list of sources, say - folded away behind a toggle
   * after the note. Without it the note is all there is, and no toggle.
   */
  details?: ReactNode;
  /** What the toggle says while the details are folded away. */
  showLabel?: string;
  /** What the toggle says while the details are shown. */
  hideLabel?: string;
}>;

/**
 * A note at the top of a page on where what it shows comes from: the first
 * thing in the content, with the same room below it on every page that has
 * one - see the Organization page, which set the pattern.
 *
 * The note itself stays short, one line where it can: it is there on every
 * visit, and a tall box pushes the page down for something most readers know
 * by the second time. Whatever goes beyond that line goes into `details`,
 * which the reader unfolds when they want it.
 *
 * @public
 */
export const PageNotice = (props: PageNoticeProps) => {
  const {
    children,
    details,
    showLabel = 'Show details',
    hideLabel = 'Hide details',
  } = props;
  const [open, setOpen] = useState(false);
  const detailsId = useId();

  return (
    <Box mb={2}>
      <Alert severity="info">
        {children}
        {details && (
          <>
            {' '}
            <Link
              component="button"
              variant="body2"
              onClick={() => setOpen(!open)}
              aria-expanded={open}
              aria-controls={detailsId}
              style={{ verticalAlign: 'baseline' }}
            >
              {open ? hideLabel : showLabel}
            </Link>
            <Collapse in={open}>
              <div id={detailsId}>{details}</div>
            </Collapse>
          </>
        )}
      </Alert>
    </Box>
  );
};
